import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, CheckCircle2, Play, RefreshCw, TrendingDown } from 'lucide-react';
import { formatDate, formatQty, type AlertSeverity } from '@erp/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader, ErrorState, EmptyState, StatCard, ProgressBar } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card, CardHeader } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';

interface Requirement {
  id: string; required_qty: number; available_qty: number; incoming_qty: number; deficit_qty: number;
  required_by: string | null; recommended_order_date: string | null; risk_orders: { order: string }[];
  sku: string; product_name: string; unit_code: string; supplier_name: string | null;
}

interface Run {
  id: string; createdAt: string; parameters: { horizonDays?: number };
  summary: { demandProducts?: number; requirements?: number; deficits?: number; totalDeficit?: number; missingBoms?: string[]; generatedAt?: string };
  requirements: Requirement[];
}

interface Alert {
  id: string; type: string; severity: string; message: string; due_date: string | null;
  status: string; created_at: string; payload: Record<string, unknown>; sku: string | null;
  product_name: string | null; unit_code: string | null;
}

interface ProjectedRow {
  id: string; sku: string; name: string; unit_code: string; physical: number; reserved: number;
  incoming: number; projected: number; min_stock: number;
}

export function PlanningPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [tab, setTab] = useState('requirements');
  const [running, setRunning] = useState(false);

  const latestQuery = useQuery({ queryKey: ['planning-latest'], queryFn: () => api<{ data: Run | null }>('/planning/latest') });
  const alertsQuery = useQuery({ queryKey: ['alerts'], queryFn: () => api<{ data: Alert[] }>('/planning/alerts') });
  const projectedQuery = useQuery({ queryKey: ['planning-projected'], queryFn: () => api<{ data: ProjectedRow[] }>('/planning/projected') });

  const run = async () => {
    setRunning(true);
    try {
      const result = await api<{ runId: string; deficits: number; totalDeficit: number }>('/planning/run', { method: 'POST', body: { horizonDays: 180 } });
      toast.success(`MRP ejecutado · ${result.deficits} déficits detectados`);
      await qc.invalidateQueries({ queryKey: ['planning-latest'] });
      await qc.invalidateQueries({ queryKey: ['alerts'] });
      await qc.invalidateQueries({ queryKey: ['planning-projected'] });
      await qc.invalidateQueries({ queryKey: ['dashboard'] });
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setRunning(false);
    }
  };

  const updateAlert = async (id: string, action: 'acknowledge' | 'resolve') => {
    try {
      await api(`/planning/alerts/${id}/${action}`, { method: 'POST' });
      toast.success('Alerta actualizada');
      await qc.invalidateQueries({ queryKey: ['alerts'] });
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const data = latestQuery.data?.data;
  const summary = data?.summary ?? {};
  const openAlerts = (alertsQuery.data?.data ?? []).filter((alert) => alert.status === 'open');

  return (
    <div>
      <PageHeader
        title="Planificación MRP"
        description="Demanda confirmada, déficit proyectado y fecha recomendada de compra"
        actions={(
          <>
            <Button variant="outline" size="sm" onClick={() => { void latestQuery.refetch(); void alertsQuery.refetch(); void projectedQuery.refetch(); }}><RefreshCw /> Actualizar</Button>
            {can('planning.run') ? <Button onClick={() => void run()} loading={running}><Play /> Ejecutar MRP</Button> : null}
          </>
        )}
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Artículos con demanda" value={summary.demandProducts ?? '—'} hint="Explosión de BOM" icon={TrendingDown} />
        <StatCard label="Déficits detectados" value={summary.deficits ?? '—'} hint="Requieren compra" icon={AlertTriangle} tone={(summary.deficits ?? 0) > 0 ? 'warning' : 'success'} />
        <StatCard label="Déficit total" value={formatQty(summary.totalDeficit ?? 0)} hint="Unidades base agregadas" icon={TrendingDown} tone="danger" />
        <StatCard label="Última ejecución" value={data ? formatDate(data.createdAt) : '—'} hint={data ? `Horizonte ${data.parameters.horizonDays ?? 180} días` : 'Sin ejecutar'} icon={CalendarClock} tone="neutral" />
      </div>

      {summary.missingBoms?.length ? (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
          Modelos sin BOM activa: {summary.missingBoms.join(', ')}
        </div>
      ) : null}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="requirements">Requerimientos</TabsTrigger>
          <TabsTrigger value="alerts">Alertas ({openAlerts.length})</TabsTrigger>
          <TabsTrigger value="projected">Stock proyectado</TabsTrigger>
        </TabsList>

        <TabsContent value="requirements">
          <Card>
            <CardHeader title="Necesidades de compra" description="Calculadas desde pedidos confirmados y reservas" />
            {latestQuery.error ? <ErrorState message={(latestQuery.error as Error).message} onRetry={() => void latestQuery.refetch()} /> : !data ? (
              <EmptyState title="Sin ejecución MRP" description="Ejecuta la planificación para calcular déficits y fechas de compra." icon={Play} action={can('planning.run') ? <Button size="sm" onClick={() => void run()}>Ejecutar MRP</Button> : undefined} />
            ) : (
              <div className="overflow-x-auto">
                <table className="table-dense w-full min-w-[1000px]">
                  <thead className="border-b border-border bg-muted/30">
                    <tr><th>Artículo</th><th className="text-right">Requerido</th><th className="text-right">Disponible</th><th className="text-right">Entrante</th><th className="text-right">Déficit</th><th>Necesario</th><th>Pedir antes</th><th>Proveedor</th></tr>
                  </thead>
                  <tbody>
                    {data.requirements.map((requirement) => (
                      <tr key={requirement.id} className={requirement.deficit_qty > 0 ? 'bg-red-50/40 dark:bg-red-500/5' : undefined}>
                        <td>
                          <p className="font-medium">{requirement.product_name}</p>
                          <p className="font-mono text-[10px] text-muted-foreground">{requirement.sku}</p>
                        </td>
                        <td className="text-right tabular-nums">{formatQty(requirement.required_qty)} {requirement.unit_code}</td>
                        <td className="text-right tabular-nums">{formatQty(requirement.available_qty)}</td>
                        <td className="text-right tabular-nums text-muted-foreground">{formatQty(requirement.incoming_qty)}</td>
                        <td className={`text-right font-semibold tabular-nums ${requirement.deficit_qty > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatQty(requirement.deficit_qty)}</td>
                        <td className="text-[11px]">{formatDate(requirement.required_by)}</td>
                        <td className="text-[11px] font-medium">{formatDate(requirement.recommended_order_date)}</td>
                        <td className="text-[11px] text-muted-foreground">{requirement.supplier_name ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="alerts">
          <Card>
            <CardHeader title="Alertas abiertas" description="Stock mínimo, rotura prevista y riesgo de plazo" />
            {alertsQuery.error ? <ErrorState message={(alertsQuery.error as Error).message} onRetry={() => void alertsQuery.refetch()} /> : openAlerts.length === 0 ? (
              <EmptyState title="Sin alertas" description="Todas las necesidades están cubiertas." icon={CheckCircle2} />
            ) : (
              <div className="divide-y divide-border">
                {openAlerts.map((alert) => (
                  <div key={alert.id} className="flex flex-wrap items-start gap-3 px-4 py-3">
                    <div className={`mt-1 flex size-8 shrink-0 items-center justify-center rounded-full ${alert.severity === 'critical' ? 'bg-red-100 text-red-600 dark:bg-red-500/10 dark:text-red-400' : 'bg-amber-100 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400'}`}>
                      <AlertTriangle className="size-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge status={alert.severity} />
                        <Badge tone="outline">{alert.type.replace(/_/g, ' ')}</Badge>
                        {alert.sku ? <span className="font-mono text-[10px] text-muted-foreground">{alert.sku}</span> : null}
                      </div>
                      <p className="mt-1 text-xs text-foreground">{alert.message}</p>
                      {alert.due_date ? <p className="mt-0.5 text-[10px] text-muted-foreground">Fecha requerida: {formatDate(alert.due_date)}</p> : null}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => void updateAlert(alert.id, 'acknowledge')}>Revisar</Button>
                      <Button variant="ghost" size="sm" onClick={() => void updateAlert(alert.id, 'resolve')}>Resolver</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="projected">
          <Card>
            <CardHeader title="Disponibilidad proyectada" description="Físico − reservas + compras entrantes" />
            {projectedQuery.error ? <ErrorState message={(projectedQuery.error as Error).message} onRetry={() => void projectedQuery.refetch()} /> : (
              <div className="max-h-[620px] overflow-auto">
                <table className="table-dense w-full">
                  <thead className="sticky top-0 border-b border-border bg-muted/80 backdrop-blur"><tr><th>Artículo</th><th className="text-right">Físico</th><th className="text-right">Reservado</th><th className="text-right">Entrante</th><th className="text-right">Proyectado</th><th>Mínimo</th><th className="w-40">Cobertura</th></tr></thead>
                  <tbody>
                    {(projectedQuery.data?.data ?? []).map((row) => {
                      const ratio = row.min_stock > 0 ? Math.min(100, Math.max(0, (row.projected / row.min_stock) * 100)) : 100;
                      return (
                        <tr key={row.id}>
                          <td><p className="font-medium">{row.name}</p><p className="font-mono text-[10px] text-muted-foreground">{row.sku}</p></td>
                          <td className="text-right tabular-nums">{formatQty(row.physical)}</td>
                          <td className="text-right tabular-nums text-muted-foreground">{formatQty(row.reserved)}</td>
                          <td className="text-right tabular-nums text-muted-foreground">{formatQty(row.incoming)}</td>
                          <td className={`text-right font-semibold tabular-nums ${row.projected < row.min_stock ? 'text-red-600' : 'text-emerald-600'}`}>{formatQty(row.projected)} {row.unit_code}</td>
                          <td className="text-right tabular-nums text-muted-foreground">{formatQty(row.min_stock)}</td>
                          <td><ProgressBar value={ratio} tone={ratio < 50 ? 'warning' : ratio < 100 ? 'primary' : 'success'} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
